import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Pagination,
  Typography,
  Chip,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Search,
  OpenInNew,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { get } from '../../services/apiService';

/**
 * Test Data Table component with filtering and pagination
 * @returns {JSX.Element} Test Data Table component
 */
const TestDataTable = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters (multiselect)
  const [automationStatusFilter, setAutomationStatusFilter] = useState([]);
  const [functionFilter, setFunctionFilter] = useState([]);
  const [subFunctionFilter, setSubFunctionFilter] = useState([]);
  const [suiteFilter, setSuiteFilter] = useState([]);
  const [adoConfig, setAdoConfig] = useState({ orgUrl: '', project: '' });
  
  // Unique values for filters
  const [uniqueValues, setUniqueValues] = useState({
    automationStatuses: [],
    functions: [],
    subFunctions: [],
    suites: []
  });
  
  // Sorting
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');

  // Fetch ADO config for building URLs
  useEffect(() => {
    const fetchAdoConfig = async () => {
      try {
        const response = await get('/api/engineering-processes/ado-config');
        if (response.success) {
          setAdoConfig({
            orgUrl: response.orgUrl || 'https://tr-legal.visualstudio.com',
            project: response.project || 'HighQ Tech'
          });
        }
      } catch (err) {
        console.error('Error fetching ADO config:', err);
        // Use defaults
        setAdoConfig({
          orgUrl: 'https://tr-legal.visualstudio.com',
          project: 'HighQ Tech'
        });
      }
    };
    fetchAdoConfig();
  }, []);

  // Fetch unique values for filters
  useEffect(() => {
    const fetchUniqueValues = async () => {
      try {
        // Fetch a larger sample to get unique values (but not all)
        const response = await get('/api/test/public/list?limit=5000&page=1');
        if (response.success && response.data) {
          const tests = response.data;
          
          // Only show allowed automation statuses
          const allowedStatuses = ['Automated-UI', 'Automated-API', 'TBD', 'Manual', 'Requires Automation'];
          const automationStatuses = allowedStatuses.filter(status => 
            tests.some(t => t.automationStatus === status)
          );
          
          // Extract unique values for other filters
          const functions = [...new Set(tests.map(t => t.function).filter(Boolean))].sort();
          const subFunctions = [...new Set(tests.map(t => t.subFunction).filter(Boolean))].sort();
          const suites = [...new Set(tests.map(t => t.testSuite).filter(Boolean))].sort();
          
          setUniqueValues({
            automationStatuses,
            functions,
            subFunctions,
            suites
          });
        }
      } catch (err) {
        console.error('Error fetching unique values:', err);
      }
    };
    fetchUniqueValues();
  }, []);

  // Fetch tests with filters and pagination
  useEffect(() => {
    const fetchTests = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          page: page.toString(),
          limit: '100',
          sortColumn: sortColumn,
          sortDirection: sortDirection
        });

        // Add array filters
        if (automationStatusFilter.length > 0) {
          automationStatusFilter.forEach(status => {
            params.append('automationStatus', status);
          });
        }
        if (functionFilter.length > 0) {
          functionFilter.forEach(func => {
            params.append('function', func);
          });
        }
        if (subFunctionFilter.length > 0) {
          subFunctionFilter.forEach(subFunc => {
            params.append('subFunction', subFunc);
          });
        }
        if (suiteFilter.length > 0) {
          suiteFilter.forEach(suite => {
            params.append('testSuite', suite);
          });
        }

        const response = await get(`/api/test/public/list?${params.toString()}`);

        if (response.success) {
          setTests(response.data || []);
          setTotalPages(response.pagination?.pages || 1);
          setTotal(response.pagination?.total || 0);
        } else {
          throw new Error(response.message || 'Failed to fetch tests');
        }
      } catch (err) {
        console.error('Error fetching tests:', err);
        setError(err.message || 'Failed to load test data');
      } finally {
        setLoading(false);
      }
    };

    fetchTests();
  }, [page, automationStatusFilter, functionFilter, subFunctionFilter, suiteFilter, sortColumn, sortDirection]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [automationStatusFilter, functionFilter, subFunctionFilter, suiteFilter]);

  // Get ADO URL for test ID
  const getAdoUrl = (testId) => {
    const orgUrl = adoConfig.orgUrl || 'https://tr-legal.visualstudio.com';
    const project = encodeURIComponent(adoConfig.project || 'HighQ Tech');
    return `${orgUrl}/${project}/_workitems/edit/${testId}`;
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sort icon for column
  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  if (loading && tests.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Automation Status</InputLabel>
          <Select
            multiple
            value={automationStatusFilter}
            onChange={(e) => setAutomationStatusFilter(e.target.value)}
            input={<OutlinedInput label="Automation Status" />}
            renderValue={(selected) => selected.join(', ')}
          >
            {uniqueValues.automationStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={automationStatusFilter.indexOf(status) > -1} />
                <ListItemText primary={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Function</InputLabel>
          <Select
            multiple
            value={functionFilter}
            onChange={(e) => setFunctionFilter(e.target.value)}
            input={<OutlinedInput label="Function" />}
            renderValue={(selected) => selected.join(', ')}
          >
            {uniqueValues.functions.map((func) => (
              <MenuItem key={func} value={func}>
                <Checkbox checked={functionFilter.indexOf(func) > -1} />
                <ListItemText primary={func} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sub Function</InputLabel>
          <Select
            multiple
            value={subFunctionFilter}
            onChange={(e) => setSubFunctionFilter(e.target.value)}
            input={<OutlinedInput label="Sub Function" />}
            renderValue={(selected) => selected.join(', ')}
          >
            {uniqueValues.subFunctions.map((subFunc) => (
              <MenuItem key={subFunc} value={subFunc}>
                <Checkbox checked={subFunctionFilter.indexOf(subFunc) > -1} />
                <ListItemText primary={subFunc} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Suite</InputLabel>
          <Select
            multiple
            value={suiteFilter}
            onChange={(e) => setSuiteFilter(e.target.value)}
            input={<OutlinedInput label="Suite" />}
            renderValue={(selected) => selected.join(', ')}
          >
            {uniqueValues.suites.map((suite) => (
              <MenuItem key={suite} value={suite}>
                <Checkbox checked={suiteFilter.indexOf(suite) > -1} />
                <ListItemText primary={suite} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                onClick={() => handleSort('id')}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  Test ADO ID
                  {getSortIcon('id')}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                onClick={() => handleSort('title')}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  Title
                  {getSortIcon('title')}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                onClick={() => handleSort('automationStatus')}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  Automation Status
                  {getSortIcon('automationStatus')}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                onClick={() => handleSort('function')}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  Function
                  {getSortIcon('function')}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                onClick={() => handleSort('subFunction')}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  Sub Function
                  {getSortIcon('subFunction')}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                onClick={() => handleSort('testSuite')}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  Suite
                  {getSortIcon('testSuite')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No tests found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tests.map((test) => (
                <TableRow key={test.id} hover>
                  <TableCell>
                    <Link
                      href={getAdoUrl(test.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {test.id}
                      <OpenInNew fontSize="small" />
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.85rem',
                        maxWidth: 400,
                        wordWrap: 'break-word',
                        whiteSpace: 'normal'
                      }}
                    >
                      {test.title || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={test.automationStatus || 'N/A'}
                      size="small"
                      color={
                        test.automationStatus === 'Automated-UI' || 
                        test.automationStatus === 'Automated-API' ||
                        test.automationStatus === 'EPAM-Automated'
                          ? 'success'
                          : test.automationStatus === 'Manual'
                          ? 'default'
                          : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>{test.function || '-'}</TableCell>
                  <TableCell>{test.subFunction || '-'}</TableCell>
                  <TableCell>{test.testSuite || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {tests.length > 0 ? (page - 1) * 100 + 1 : 0} to {Math.min(page * 100, total)} of {total} tests
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>
    </Box>
  );
};

export default TestDataTable;

